/**
 * Determines the test schedule status based on the next test due date and overdue date.
 *
 * @param nextTestOverdueDate - The ISO date string representing when the test becomes overdue
 * @param nextTestDueDate - The ISO date string representing when the test becomes due
 * @returns 'due' if the due date has been reached but not yet overdue, 'overdue' if the overdue date has passed, or '-' if no schedule or not yet due
 */

type TestScheduleStatusReturnType = 'due' | 'overdue' | '-';

export type ScheduledReviewStatus = 'due' | 'overdue' | 'pending';

/**
 * Determines the scheduled review status for My Items badges.
 *
 * Mirrors getTestScheduleStatus grace-period semantics (overdue only once the
 * overdue date has passed), but with My Items' three-state model: 'pending'
 * until the due date is reached, and immediate 'overdue' when no grace period
 * is configured (no overdue date).
 */
export const getScheduledReviewStatus = (
  nextTestOverdueDate?: string | null,
  nextTestDueDate?: string | null
): ScheduledReviewStatus => {
  const now = new Date();

  if (!nextTestDueDate || new Date(nextTestDueDate) > now) {
    return 'pending';
  }

  if (!nextTestOverdueDate || new Date(nextTestOverdueDate) < now) {
    return 'overdue';
  }

  return 'due';
};

export const getTestScheduleStatus = (
  nextTestOverdueDate?: string | null,
  nextTestDueDate?: string | null
): TestScheduleStatusReturnType => {
  if (!nextTestOverdueDate) {
    return '-';
  }

  const now = new Date();
  const overdueDate = new Date(nextTestOverdueDate);

  if (overdueDate < now) {
    return 'overdue';
  }

  if (nextTestDueDate) {
    const dueDate = new Date(nextTestDueDate);
    if (dueDate > now) {
      return '-';
    }
  }

  return 'due';
};
